import { Faq as FaqType } from 'libs/http/api/content/content.types';

import { useInt } from 'hooks/useInt';

import { Faq } from 'ui/atoms/Faq/Faq';
import { Section } from 'ui/molecules/Section/Section';

import { CaseStudyCard } from 'landing-pages/shared/CaseStudyCard/CaseStudyCard';

import styles from './InvestmentScreeningQuoteFaq.module.scss';

const FAQS = [
  {
    id: 'data-source',
    q: 'Where does the data actually come from?',
    a: 'Primary, government-sourced. We collect from 400+ company registries and official gazettes across 200+ countries, and every field on a profile carries the registry it came from and the date it was filed. Where a value is calculated rather than filed, such as EBITDA derived from the accounts, it is labelled as derived on the record and in the API response.',
  },
  {
    id: 'countries-registries',
    q: 'Which countries and registries are covered?',
    a: '400+ company registries and official gazettes across 200+ countries, resolving to more than 600 million company profiles. Depth is not uniform and we do not pretend otherwise: some registries publish full statutory accounts, shareholders and beneficial ownership, others publish identity, status and officers only. The data dictionary lists what is available per jurisdiction, so you can check your target markets before committing to anything.',
  },
  {
    id: 'screen-currency',
    q: 'How current is a screen?',
    a: 'Registry sources are synced daily and saved screens re-run against the latest sync. Publication cadence is set by each registry, so a filing that appears in Companies House on Tuesday reaches your screen within one business day, while a jurisdiction that publishes monthly moves at that pace. Sync status per source is visible in the platform.',
  },
  {
    id: 'private-companies',
    q: 'Do you cover private companies that have never raised capital?',
    a: 'That is the point of the coverage model. The universe starts from the legal obligation to register and file, not from funding announcements, so companies that have never raised and never issued a press release sit on the same footing as venture-backed ones. This is where most mid-market deal flow lives and where funding-first platforms are thinnest.',
  },
  {
    id: 'ownership-screening',
    q: 'Can we screen on ownership as well as financials?',
    a: 'Yes, and for origination it is often the more useful filter. Screens can be built on ownership type, whether a sponsor appears on the register, concentration of holdings, presence of an employee trust, foreign parentage, or a change of majority holder inside a window you choose. Those filters run against the shareholder register and the control chain rather than an inferred label.',
  },
  {
    id: 'deal-history',
    q: 'Does it cover deal history and prior transactions?',
    a: 'Acquisitions, disposals, majority changes and funding events across the group, each tied to the filing or disclosure that evidences it. Where a transaction leaves a registry trace, such as a share allotment, a change of control filing or a note in the accounts, it appears on the timeline with that document referenced.',
  },
  {
    id: 'warehouse-models',
    q: 'Can we put this in our own warehouse and models?',
    a: 'Yes. Bulk delivery to S3, GCS or Azure on a schedule you set, delivered with registration number and VAT alongside our own ID, so the join does not depend on a proprietary key, and licensed for redistribution into your own products and models. Commercial terms depend on scope, so the exact licence is agreed at contract.',
  },
  {
    id: 'existing-licences',
    q: 'How does this sit alongside what we already licence?',
    a: 'Most deal teams keep a market-intelligence subscription for the funded market and run us underneath it as the primary record: entity resolution, filed financials, ownership chains, officers and change monitoring. Because every record carries its registration number and VAT number, joining to your existing vendor IDs is a mapping table rather than a migration.',
  },
  {
    id: 'pricing',
    q: 'How is it priced?',
    a: 'By data scope and volume rather than by seat, so a screen covering six countries and one sector costs less than one covering the whole of Europe. Platform access, API and bulk delivery are priced separately, and redistribution rights are agreed at contract. Tell us the markets, the fields and the volumes and you get a number.',
  },
  {
    id: 'evaluation',
    q: 'What does an evaluation look like?',
    a: 'Send a longlist or a screen definition. We run it against live data, return the resolved entities with financials, ownership and provenance, and you compare it against whatever you use now on the same names. Most teams know inside a week.',
  },
];

export const InvestmentScreeningQuoteFaq = () => {
  const { t } = useInt();

  const items: FaqType[] = FAQS.map((faq, i) => ({
    id: faq.id,
    question: t(faq.q),
    answer: t(faq.a),
    default_opened: i === 0,
  }));

  return (
    <>
      <CaseStudyCard
        company="Finquest"
        title={t('Finquest: Enhancing Investment Opportunities')}
        summary={t(
          'Finquest connects investors with high-potential private companies worldwide, screening opportunities against comprehensive, verified company data rather than self-reported figures.',
        )}
        href="/finquest-enhancing-investment-opportunities-with-comprehensive-company-data"
      />

      <Section className={styles.root}>
        <Faq title={t('Before you test it')} data={items} />
      </Section>
    </>
  );
};
